import {
    CHART_OPTIONS_META_DATA,
    DEFAULT_COLOR,
    MIN_DIAMETER,
    MAX_DIAMETER,
    COMPARE_COLOR,
} from "app/pages/industry-analysis/players/table/bubble-chart/const";
import {
    ETypes,
    type IChartData,
    type SelectedEntitiesColorMapT,
    type SelectedEntitiesIDsT,
} from "app/pages/industry-analysis/players/table/bubble-chart/types";
import type { IRawData, IRecord } from "app/pages/industry-analysis/players/table/types";
import { minMaxNormalizationHof } from "app/UtilitiesAndConstants/UtilityFunctions/featureScaling";

type SegmentNameGetter = (id: string) => string | undefined;

const getAttributes = (type: ETypes) => {
    const xAttribute = CHART_OPTIONS_META_DATA[type].xAxis.attribute;
    const yAttribute = CHART_OPTIONS_META_DATA[type].yAxis.attribute;
    const zAttribute = CHART_OPTIONS_META_DATA[type].zAxis.attribute;

    return { xAttribute, yAttribute, zAttribute };
};

type AttributeKeys = {
    xAttribute: string;
    yAttribute: string;
    zAttribute: string;
};

const hasRequiredAttributes = (item: IRecord, attributes: AttributeKeys): boolean => {
    const itemRecord = item as unknown as Record<string, unknown>;
    return Object.values(attributes).every((attr) => itemRecord[attr] !== undefined && itemRecord[attr] !== null);
};

export const getBubbleChartCompareByIdMap = (
    rawData: IRawData,
    isPeriodOverPeriod: boolean,
): Map<string, IRecord> | undefined => {
    const compareList = rawData.CompareData;
    if (isPeriodOverPeriod && compareList && compareList.length > 0) {
        return new Map(compareList.map((row) => [row.Id, row]));
    }
    return undefined;
};

export const recordHasCompleteDataForBubbleChartType = (
    item: IRecord,
    type: ETypes,
    compareById: Map<string, IRecord> | undefined,
): boolean => {
    const attributes = getAttributes(type);
    if (!hasRequiredAttributes(item, attributes)) {
        return false;
    }
    if (compareById) {
        const compareRow = compareById.get(item.Id);
        if (!compareRow || !hasRequiredAttributes(compareRow, attributes)) {
            return false;
        }
    }
    return true;
};

export const sortLegendRecordsByConversionRateDataAvailability = (
    data: IRecord[],
    compareById: Map<string, IRecord> | undefined,
): IRecord[] =>
    data
        .map((item, originalIndex) => ({ item, originalIndex }))
        .toSorted((a, b) => {
            const aOk = recordHasCompleteDataForBubbleChartType(a.item, ETypes.ConversionRate, compareById);
            const bOk = recordHasCompleteDataForBubbleChartType(b.item, ETypes.ConversionRate, compareById);
            if (aOk !== bOk) {
                return aOk ? -1 : 1;
            }
            return a.originalIndex - b.originalIndex;
        })
        .map(({ item }) => item);

export const getConversionRateDefaultLegendIds = (
    rawData: IRawData,
    isPeriodOverPeriod: boolean,
    maxSelection: number,
): string[] => {
    if (!rawData?.Data?.length) {
        return [];
    }

    const compareById = getBubbleChartCompareByIdMap(rawData, isPeriodOverPeriod);

    const ids: string[] = [];
    for (const item of rawData.Data) {
        if (!recordHasCompleteDataForBubbleChartType(item, ETypes.ConversionRate, compareById)) {
            continue;
        }
        ids.push(item.Id);
        if (ids.length >= maxSelection) {
            break;
        }
    }
    return ids;
};

export const getBubbleChartInitialSelectionIds = (
    rawData: IRawData,
    type: ETypes,
    isPeriodOverPeriod: boolean,
    maxSelection: number,
): string[] => {
    if (!rawData?.Data?.length) {
        return [];
    }
    if (type === ETypes.ConversionRate) {
        return getConversionRateDefaultLegendIds(rawData, isPeriodOverPeriod, maxSelection);
    }
    return rawData.Data.slice(0, maxSelection).map(({ Id }) => Id);
};

const getZAxisValues = (
    rawData: IRawData,
    zAttribute: string,
    isPeriodOverPeriod: boolean,
    attributes: AttributeKeys,
    selectedEntitiesIDs: SelectedEntitiesIDsT,
): number[] => {
    const validCurrentItems = rawData.Data.filter(
        (item) => selectedEntitiesIDs.has(item.Id) && hasRequiredAttributes(item, attributes),
    );

    const currentPeriodValues = validCurrentItems.map((item) => item[zAttribute] as number);

    if (isPeriodOverPeriod) {
        const validCompareItems = (rawData.CompareData || []).filter(
            (item) => selectedEntitiesIDs.has(item.Id) && hasRequiredAttributes(item, attributes),
        );

        const comparePeriodValues = validCompareItems.map((item) => item[zAttribute] as number);

        return [...currentPeriodValues, ...comparePeriodValues];
    }

    return currentPeriodValues;
};

const getMinMaxNormalization = (zAxisValues: number[]) => {
    if (zAxisValues.length === 0) {
        return () => MIN_DIAMETER;
    }

    const maxZ = Math.max(...zAxisValues);
    const minZ = Math.min(...zAxisValues);
    const fromRange = { from: minZ, to: maxZ };
    const toRange = { from: MIN_DIAMETER, to: MAX_DIAMETER };

    return minMaxNormalizationHof(fromRange, toRange);
};

export const parseRawData = (
    rawData: IRawData,
    type: ETypes,
    selectedEntitiesIDs: SelectedEntitiesIDsT,
    selectedEntitiesColorMap: SelectedEntitiesColorMapT,
    segmentNameGetter: SegmentNameGetter = () => undefined,
    isPeriodOverPeriod: boolean = false,
): IChartData => {
    if (!rawData) {
        return [];
    }
    const attributes = getAttributes(type);
    const filteredItems = rawData.Data.filter((item) => selectedEntitiesIDs.has(item.Id));
    const filteredCompareItems =
        rawData.CompareData && rawData.CompareData.filter((item) => selectedEntitiesIDs.has(item.Id));
    const zAxisValues = getZAxisValues(
        rawData,
        attributes.zAttribute,
        isPeriodOverPeriod,
        attributes,
        selectedEntitiesIDs,
    );
    const minMaxNormalization = getMinMaxNormalization(zAxisValues);

    const validItems = filteredItems.filter((item) => hasRequiredAttributes(item, attributes));

    return validItems.map((item) => {
        const { Domain: name, Favicon: favicon, Id: id } = item;

        const comparedItem =
            isPeriodOverPeriod && filteredCompareItems
                ? filteredCompareItems.find((compareItem) => compareItem.Id === id)
                : undefined;

        const chartData = {
            name,
            subtitle: segmentNameGetter ? segmentNameGetter(id) : undefined,
            favicon,
            rawCurrentData: item as unknown as Record<string, unknown>,
            rawCompareData: comparedItem as unknown as Record<string, unknown>,
            data: [],
            index: id,
        };

        if (hasRequiredAttributes(item, attributes)) {
            chartData.data.push({
                x: item[attributes.xAttribute],
                y: item[attributes.yAttribute],
                z: item[attributes.zAttribute],
                displayZ: item[attributes.zAttribute] ? minMaxNormalization(item[attributes.zAttribute]) : MIN_DIAMETER,
                color: selectedEntitiesColorMap.get(id) ?? DEFAULT_COLOR,
                isPreviousPeriod: false,
            });
        }

        if (isPeriodOverPeriod && filteredCompareItems) {
            if (comparedItem && hasRequiredAttributes(comparedItem, attributes)) {
                chartData.data.push({
                    x: comparedItem[attributes.xAttribute],
                    y: comparedItem[attributes.yAttribute],
                    z: comparedItem[attributes.zAttribute],
                    displayZ: comparedItem[attributes.zAttribute]
                        ? minMaxNormalization(comparedItem[attributes.zAttribute])
                        : MIN_DIAMETER,
                    color: selectedEntitiesColorMap.get(id) ?? COMPARE_COLOR,
                    isPreviousPeriod: true,
                });
            }
        }

        return chartData;
    });
};
